import { DishResponse, GetAllResolvedEdiblesResponse } from "api-types";

import React, { useCallback, useMemo, useState } from "react";

import type { SxProps, Theme } from "@mui/material";
import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

import { CheckCircleRounded, NotInterestedRounded } from "@mui/icons-material";

import { useActionTracking } from "../../../analytics/actions-context";
import { DatadogActionNames } from "../../../analytics/datadog-action-names";
import { useDeviceDimensions } from "../../../hooks/useDeviceDimensions";
import { useTranslation } from "../../../i18n";
import { ImagePreview } from "../../ImagePreview/ImagePreview";
import { DishInfoDrawer } from "../DishInfoDrawer/DishInfoDrawer";
import { ProductDrawer } from "../ProductDrawer/ProductDrawer";
import { MappedProduct } from "../ProductDrawer/product.types";
import { RelatedIngredientsDrawer } from "../RelatedIngredientsDrawer/RelatedIngredientsDrawer";
import ArrowRightIcon from "../assets/arrow-right.svg";
import MayContainIcon from "../assets/may-contain.svg";
import {
  hasDietSelection,
  meetsDietSelection,
  useFilterMenu,
} from "../filters";
import { DishMetaRow } from "./DishMetaRow";
import { DishTitle } from "./DishTitle";
import { SpiceAndPrice } from "./SpiceAndPrice";

// Local helpers & hooks (kept in-file to avoid new files)
const TAG_INTENT = {
  RECOMMEND: "recommend" as const,
  NEUTRAL: "neutral" as const,
};

/**
 * Read aloud, never drawn. `display: none` and `visibility: hidden` take text
 * out of the accessibility tree along with the layout, so this clips it to a
 * pixel instead.
 */
const SPOKEN_ONLY: SxProps<Theme> = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
};

/**
 * Filters the source array to only include items whose key matches any key in the selected array.
 * Provides a generic utility to match items by specified keys from two arrays.
 * Returns empty array if inputs are invalid or keys are missing.
 */
function matchById<
  S extends Record<string, any>,
  T extends Record<string, any>,
  KS extends keyof S,
  KT extends keyof T
>(source: S[], selected: T[], srcKey: KS, selKey: KT): S[] {
  if (!Array.isArray(source) || !Array.isArray(selected) || !srcKey || !selKey)
    return [];
  const toKey = (v: unknown) => String(v as string | number | bigint | symbol);
  const selIds = new Set(selected.map((s) => toKey(s[selKey])));
  return source.filter((s) => selIds.has(toKey(s[srcKey])));
}

/**
 * Derives matched ingredients, allergens, tags, and preferences for a dish based on current filters.
 * Memoizes results by dish version and filter selections.
 * Determines flags indicating presence of matching allergens and preferences.
 */
export function useDishMatches(
  dish: DishResponse,
  filterMenu: ReturnType<typeof useFilterMenu>
) {
  const matchedIngredients = React.useMemo(() => {
    return matchById(
      dish.ingredients,
      filterMenu.selectedIngredients,
      "igfdTaxIngredientId",
      "igfdTaxIngredientId"
    );
  }, [dish.ingredients, filterMenu.selectedIngredients]);

  const matchedAllergens = React.useMemo(() => {
    return matchById(
      dish.allergens,
      filterMenu.selectedAllergens,
      "igfdTaxAllergenId",
      "igfdTaxAllergenId"
    );
  }, [dish.allergens, filterMenu.selectedAllergens]);

  const matchedRecommendTags = React.useMemo(() => {
    if (!Array.isArray(dish.tags) || !Array.isArray(filterMenu.selectedTags))
      return [];
    const selectedIds = new Set(filterMenu.selectedTags.map((t) => t.id));
    return dish.tags.filter(
      (t) => t.displayIntent === TAG_INTENT.RECOMMEND && selectedIds.has(t.id)
    );
  }, [dish.tags, filterMenu.selectedTags]);

  const matchedNeutralTags = React.useMemo(() => {
    if (!Array.isArray(dish.tags) || !Array.isArray(filterMenu.selectedTags))
      return [];
    const selectedIds = new Set(filterMenu.selectedTags.map((t) => t.id));
    return dish.tags.filter(
      (t) => t.displayIntent === TAG_INTENT.NEUTRAL && selectedIds.has(t.id)
    );
  }, [dish.tags, filterMenu.selectedTags]);

  const matchedPreferences = React.useMemo(() => {
    if (!filterMenu.selectedPreference)
      return [] as DishResponse["preferences"];
    return dish.preferences.filter(
      (p) =>
        p.igfdTaxPreferenceId ===
        filterMenu.selectedPreference?.igfdTaxPreferenceId
    );
  }, [dish.preferences, filterMenu.selectedPreference]);

  const matchedMayContainAllergens = React.useMemo(() => {
    if (!filterMenu.showMayContainAllergens) {
      // This feature is disabled
      return [];
    }
    return filterMenu.selectedAllergens.flatMap((selectedAllergen) =>
      dish.mayContainAllergens.filter(
        (dishMayContainAllergen) =>
          dishMayContainAllergen.igfdTaxAllergenId ===
          selectedAllergen.igfdTaxAllergenId
      )
    );
  }, [
    dish.mayContainAllergens,
    filterMenu.selectedAllergens,
    filterMenu.showMayContainAllergens,
  ]);

  const hasMatchingAllergens =
    matchedAllergens.length > 0 || matchedIngredients.length > 0;
  const showMayContains =
    filterMenu.showMayContainAllergens && matchedMayContainAllergens.length > 0;

  return {
    matchedIngredients,
    matchedAllergens,
    matchedMayContainAllergens,
    matchedRecommendTags,
    matchedNeutralTags,
    matchedPreferences,
    hasMatchingAllergens,
    showMayContains,
  };
}

interface DishProps {
  dish: DishResponse;
  restaurantName: string;
  linkedItems?: GetAllResolvedEdiblesResponse;
  isIngredientFilteringEnabled?: boolean;
  priceFormatter: (priceInLowestDenomination: number) => string;
  /**
   * True when this dish offers choices, so tapping it should open those
   * rather than its own detail. Kept separate from `optionsSummary`, which a
   * dish can omit while still having options, and from `onOpenOptions`, which
   * Storybook's action addon supplies to any story that leaves it out.
   */
  hasOptions?: boolean;
  /**
   * How many of this dish's own options suit the guest, already worded by the
   * caller. It joins the quiet second line rather than carrying a chip: a
   * count is not a safety verdict, and on a loaded row it should not compete
   * with one.
   */
  optionsSummary?: string;
  /**
   * True when a required group leaves nothing orderable, which moves the
   * summary out of the quiet line and up beside the allergens.
   */
  optionsSummaryBlocked?: boolean;
  /** Shows that summary as a chip, for a row listed inside a sheet. */
  optionsSummaryAsChip?: boolean;
  /**
   * Overrides the thumbnail edge. A dish listed as one option among several is
   * being compared, not sold, so it carries a smaller picture than the same
   * dish does as a headline row on the menu.
   */
  thumbnailSize?: string;
  /** Opens this dish's options. Only meaningful alongside `optionsSummary`. */
  onOpenOptions?: () => void;
  containerRef?: React.RefObject<HTMLDivElement>;
}

/**
 * A component for a dish in the menu
 */
export const Dish = ({
  dish,
  linkedItems,
  isIngredientFilteringEnabled = false,
  priceFormatter,
  hasOptions = false,
  optionsSummary,
  optionsSummaryBlocked,
  optionsSummaryAsChip,
  thumbnailSize,
  onOpenOptions,
  containerRef,
}: DishProps) => {
  const { t } = useTranslation();
  const {
    palette: { oscar },
  } = useTheme();
  const filterMenu = useFilterMenu();
  const { isMobile } = useDeviceDimensions();

  const [openProductDrawer, setOpenProductDrawer] =
    useState<MappedProduct | null>(null);
  const [openAllergenIngredientsDrawer, setOpenAllergenIngredientsDrawer] =
    useState(false);
  const price = useMemo(() => {
    return priceFormatter(dish.priceInLowestDenomination);
  }, [dish.priceInLowestDenomination, priceFormatter]);

  // Depend on dish.updatedAt (when available) so live changes from the restaurant re-compute matches and disclaimers while the user is viewing.
  const {
    matchedIngredients,
    matchedAllergens,
    matchedRecommendTags,
    matchedNeutralTags,
    matchedPreferences,
    matchedMayContainAllergens,
    hasMatchingAllergens,
    showMayContains,
  } = useDishMatches(dish, filterMenu);

  const [dishInfoDrawerOpen, setDishInfoDrawerOpen] = useState(false);

  const compatibilityType = useMemo(() => {
    return matchedAllergens.length > 0 || matchedIngredients.length > 0
      ? "allergy"
      : showMayContains
      ? "may-contain"
      : // A tag the guest picked, such as Halal, is a diet the restaurant
      // defines itself. The tick needs the dish to carry all of them.
      hasDietSelection(filterMenu) && meetsDietSelection(dish, filterMenu)
      ? "preference"
      : "standard";
  }, [
    dish,
    filterMenu,
    matchedAllergens,
    matchedIngredients,
    matchedMayContainAllergens,
    showMayContains,
  ]);

  const specialIcon =
    compatibilityType === "allergy" ? (
      <NotInterestedRounded
        fontSize="small"
        sx={{ color: oscar.light.content.danger }}
      />
    ) : compatibilityType === "may-contain" ? (
      <img src={MayContainIcon} alt="" style={{ width: 20 }} />
    ) : compatibilityType === "preference" ? (
      <CheckCircleRounded
        fontSize={"small"}
        sx={{ color: oscar.light.icon.accent }}
      />
    ) : null;

  // One hue, two jobs. A glyph is legible at 3:1, which these clear; the same
  // red or green set as a 16px dish name is not, and the name is the part the
  // guest actually reads.
  const highlightColor =
    compatibilityType === "allergy"
      ? oscar.light.icon.danger
      : compatibilityType === "preference"
      ? oscar.light.icon.accent
      : undefined;
  const highlightTextColor =
    compatibilityType === "allergy"
      ? oscar.light.content.dangerText
      : compatibilityType === "preference"
      ? oscar.light.content.accentText
      : undefined;

  /**
   * What the row means but does not say. Rendered hidden inside the row rather
   * than as an aria-label: a label replaces the button's contents instead of
   * adding to them, so labelling the row would have silenced its price,
   * description and choices.
   *
   * Only the allergen case earns it. In summary the chip says "Not suitable"
   * without naming anything, and with details on the names are chips with no
   * verdict over them; either way a word is missing. A may-contain row already
   * reads as its own warning, and repeating it verbatim is noise, not access.
   */
  const spokenVerdict = useMemo(() => {
    if (compatibilityType !== "allergy") return undefined;
    return t("dish.notSuitableNamed", {
      names: [
        ...matchedAllergens.map((allergen) => allergen.allergenName),
        ...matchedIngredients.map((i) => i.ingredientName),
      ].join(", "),
    });
  }, [compatibilityType, matchedAllergens, matchedIngredients, t]);

  const { trackDishSelected } = useActionTracking();
  const onDishClick = useCallback(() => {
    trackDishSelected(dish.id);
    // A dish that offers choices opens those instead of its own detail, since
    // the choices are what the guest still has to decide.
    if (hasOptions && onOpenOptions) {
      onOpenOptions();
      return;
    }
    setDishInfoDrawerOpen(true);
  }, [dish.id, hasOptions, onOpenOptions, trackDishSelected]);

  const linkedDish = linkedItems?.dishes?.[dish.id];

  const optionalIngredientIds = useMemo(
    () => new Set(linkedDish?.optionalIngredientIds ?? []),
    [linkedDish]
  );

  const showRelatedIngredientsFromProducts = useMemo(() => {
    if (!matchedIngredients.length) return false;
    const directIngredientIds = new Set(
      (linkedDish?.directlyLinkedIngredients ?? []).map(
        (i) => i.igfdTaxIngredientId
      )
    );
    return matchedIngredients.some((mi) => {
      const id = mi.igfdTaxIngredientId;
      return optionalIngredientIds.has(id) && !directIngredientIds.has(id);
    });
  }, [matchedIngredients, linkedDish, optionalIngredientIds]);

  return (
    <>
      {/* A real button, not a clickable Stack: this row is how a guest opens
          what a dish contains, and on an allergen menu that cannot be
          pointer-only. */}
      <ButtonBase
        type="button"
        aria-haspopup="dialog"
        sx={{
          alignItems: "stretch",
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          padding: "16px 0",
          textAlign: "left",
          width: "100%",
          "&:focus-visible": {
            outline: `3px solid ${oscar.light.icon.accent}`,
            outlineOffset: "4px",
          },
        }}
        onClick={onDishClick}
        data-dd-action-name={DatadogActionNames.dishSelected(dish.name)}
      >
        {/* First, so the verdict is heard before the dish is described. On an
            allergen menu that ordering is the whole point of the row. */}
        {spokenVerdict ? (
          <Box component="span" sx={SPOKEN_ONLY}>
            {spokenVerdict}
          </Box>
        ) : null}

        {/* Title/description sit in a column with the image beside them. The
            chip and everything below it live in that same column (not as
            siblings of this row) so their position tracks the text's own
            height rather than the image's — a short description otherwise
            leaves a gap sized by the taller image, not by the text above it. */}
        <Stack flexDirection="row" alignItems="flex-start" gap={2}>
          <Stack direction="column" gap={1.5} flex={1} minWidth={0}>
            <Stack
              alignItems="center"
              flexDirection="row"
              gap={2}
              justifyContent="space-between"
              minWidth={0}
            >
              <HeaderRow>
                {specialIcon}
                <DishTitle name={dish.name} titleColor={highlightTextColor} />
              </HeaderRow>
              {!!dish.spiceLevel || price ? (
                <SpiceAndPrice price={price} spiceLevel={dish.spiceLevel} />
              ) : null}
            </Stack>
            {dish.description ? (
              <Typography
                color={oscar.light.content.content1}
                variant="bodyRLight"
                sx={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  overflow: "hidden",
                }}
              >
                {dish.description}
              </Typography>
            ) : null}
            {/* Calories, the options count, diets, tags and the allergen
                verdict used to be five stacked blocks. They are two lines
                now: what the guest cannot eat, then everything else. */}
            <DishMetaRow
              matchedAllergens={matchedAllergens}
              matchedIngredients={matchedIngredients}
              matchedMayContainAllergens={matchedMayContainAllergens}
              matchedPreferences={matchedPreferences}
              matchedRecommendTags={matchedRecommendTags}
              matchedNeutralTags={matchedNeutralTags}
              optionalIngredientIds={optionalIngredientIds}
              hasMatchingAllergens={hasMatchingAllergens}
              showMayContains={showMayContains}
              optionsSummary={optionsSummary}
              optionsSummaryBlocked={optionsSummaryBlocked}
              optionsSummaryAsChip={optionsSummaryAsChip}
              caloriesInKcal={dish.caloriesInKcal}
            />
          </Stack>
          {/* Full colour whatever the verdict: the chips say what the guest
              cannot eat, and a drained photo only hides the food. */}
          {dish.imageUrl ? (
            <ImagePreview
              imageUrl={dish.imageUrl}
              height={thumbnailSize ?? "90px"}
              width={thumbnailSize ?? (isMobile ? "90px" : "160px")}
            />
          ) : null}
          {/* Last in the row, past the image, so every row's arrow lands on
              one edge whether or not the dish has a picture. */}
          <img src={ArrowRightIcon} alt="" style={{ flexShrink: 0 }} />
        </Stack>
      </ButtonBase>
      <DishInfoDrawer
        dish={dish}
        formattedPrice={price}
        specialIcon={specialIcon}
        highlightColor={highlightColor}
        highlightTextColor={highlightTextColor}
        matchedRecommendTags={matchedRecommendTags}
        matchedNeutralTags={matchedNeutralTags}
        matchedAllergens={matchedAllergens}
        matchedMayContainAllergens={matchedMayContainAllergens}
        isIngredientFilteringEnabled={isIngredientFilteringEnabled}
        matchedPreferences={matchedPreferences}
        matchedIngredients={matchedIngredients}
        optionalIngredientIds={optionalIngredientIds}
        showMayContains={showMayContains}
        open={dishInfoDrawerOpen}
        onOpenAllergenIngredients={() => setOpenAllergenIngredientsDrawer(true)}
        onClose={() => setDishInfoDrawerOpen(false)}
        showCalories={filterMenu.showCalories}
        containerRef={containerRef}
        showRelatedIngredientsFromProducts={showRelatedIngredientsFromProducts}
      />
      <RelatedIngredientsDrawer
        dish={dish}
        containerRef={containerRef}
        linkedItems={linkedItems}
        matchedAllergens={matchedAllergens}
        matchedIngredients={matchedIngredients}
        open={openAllergenIngredientsDrawer}
        onClose={() => setOpenAllergenIngredientsDrawer(false)}
        onProductClick={(product) => setOpenProductDrawer(product)}
      />
      <ProductDrawer
        product={openProductDrawer}
        linkedItems={linkedItems}
        open={!!openProductDrawer}
        onClose={() => setOpenProductDrawer(null)}
      />
    </>
  );
};

const HeaderRow = styled(Stack)(() => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 8,
  // Takes the room the price does not, and wraps inside it. Without the flex
  // the title holds the row open at its widest word and pushes the price out
  // of the column, under the photo; without minWidth it ellipsises instead of
  // wrapping.
  flex: 1,
  minWidth: 0,
}));
